import { Autocomplete, Grid, Slider, Switch, TextField } from "@mui/material";
import Divider from "@material-ui/core/Divider";
import { useMutation } from "@tanstack/react-query";
import SoftBox from "components/SoftComponents/SoftBox";
import SoftButton from "components/SoftComponents/SoftButton";
import SoftInput from "components/SoftComponents/SoftInput";
import SoftTypography from "components/SoftComponents/SoftTypography";
import PropTypes from "prop-types";
import { useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import deviceAPI from "services/api/bks";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import bksAPI from "services/api/bks";


function DeviceSettingsForm({ selected_device, tags, deviceLayout }) {

    bksAPI.loadToken(useAuth0());

    const formRef = useRef(null);
    const { mutateAsync } = useMutation({
        mutationFn: (values) => deviceAPI.updateDeviceData(values)
    })

    const navigate = useNavigate();

    const defaultValues = {
        uuid: selected_device.uuid,
        device_id: selected_device.device_id,
        organisation_id: selected_device.organisation_id,
        db_type: selected_device.db_type,
        name: selected_device.name,
        serial_number: selected_device.serial_number,
        active: selected_device.active,
        position: selected_device.position,
        site_id: selected_device.stop != null ? (selected_device.stop?.stop_id ? selected_device.stop.stop_id : selected_device.stop.site_id) : null,
        stop_label: selected_device.stop != null ? selected_device.stop?.stop_label : null,
        tts_volume: selected_device.tts.volume
    };

    const {
        control,
        register,
        handleSubmit,
        formState: { errors },
        setValue
    } = useForm({
        defaultValues: defaultValues
    });

    const triggerFormSubmit = () => {
        formRef.current.dispatchEvent(
            new Event("submit", { cancelable: true, bubbles: true })
        );
    }

    const triggerDeviceReboot = async () => {
        try {
            var response = await bksAPI.reboot(selected_device.uuid);
            if (!response) {
                throw Error;
            }

            await Swal.fire({
                icon: "success",
                title: `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Device Rebooting!</div>`,
                showConfirmButton: false,
                timer: 2000
            });

        }
        catch (error) {
            Swal.close();

            console.log(error);
            Swal.fire({
                icon: "error",
                title: `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Error</div>`,
                text: "There was a problem rebooting the device.",
            });
        }
    }

    async function submitForm(formData) {
        console.log(formData);
        // 1. Display the "Saving..." loading state
        Swal.fire({
            title: `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Saving...</div>`,
            allowOutsideClick: false,
            showConfirmButton: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });

        try {
            await mutateAsync(formData);
            Swal.close();

            await Swal.fire({
                icon: "success",
                title: `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Saved!</div>`,
                showConfirmButton: false,
                timer: 2000
            });

            // navigate(0);
        } catch (error) {
            // Explicitly close the loading spinner if an error occurs
            Swal.close();

            console.log(error)

            // Handle failure state
            Swal.fire({
                icon: "error",
                title: `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Error</div>`,
                text: "There was a problem saving your data.",
            });
        }
    }

    return (
        <form ref={formRef} onSubmit={handleSubmit((data) => { submitForm(data); })} style={{ height: '100%' }}>
            <SoftBox display="flex" justifyContent="space-between" flexDirection="column" height="100%">
                <SoftBox component="form">
                    <SoftTypography p={2} sx={{ background: '#f9f9f9' }} variant={deviceLayout == 'mobile' ? "h5" : "h4"}>Device Settings</SoftTypography>
                    <Divider />
                    <Grid container spacing={2} px={2} pt={2} pb={1}>
                        <Grid item xxs={12} lg={6}>
                            <SoftBox pl={1} my={0.5}>
                                <SoftTypography variant="body2" fontWeight='bold'>Device Name</SoftTypography>
                            </SoftBox>
                            <SoftBox>
                                <SoftInput placeholder="Device Name" {...register('name')} />
                            </SoftBox>
                        </Grid>
                        <Grid item xxs={12} lg={6}>
                            <SoftBox pl={1} my={0.5}>
                                <SoftTypography variant="body2" fontWeight='bold'>Serial Number</SoftTypography>
                            </SoftBox>
                            <SoftBox>
                                <SoftInput placeholder="Serial Number" {...register('serial_number')} />
                            </SoftBox>
                        </Grid>
                    </Grid>
                    {selected_device.stop != null && (
                        <Grid container spacing={2} px={2} py={1}>
                            <Grid item xxs={12} lg={6}>
                                <SoftBox pl={1} my={0.5}>
                                    <SoftTypography variant="body2" fontWeight='bold'>Stop ID</SoftTypography>
                                </SoftBox>
                                <SoftBox>
                                    <Controller
                                        name="site_id"
                                        control={control} // Make sure to destructure 'control' from useForm()
                                        render={({ field: { onChange, value, ref } }) => (
                                            <Autocomplete
                                                id="stop-id-select"
                                                options={tags?.stops}
                                                // Display both ID and Description in the dropdown
                                                getOptionLabel={(option) =>
                                                    option ? `${option.stop_id} - ${option.gtfs.stop_name}` : ''
                                                }
                                                isOptionEqualToValue={(option, val) => option.stop_id === val.stop_id}
                                                // Map the simple form value back to the full option object for the UI
                                                value={tags?.stops.find((opt) => opt.stop_id === value) || null}
                                                // Extract just the stop_id to save in the form data
                                                onChange={(event, newValue) => {
                                                    onChange(newValue ? newValue.stop_id : null);
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        inputRef={ref} // Connects RHF ref for error focusing
                                                        variant="outlined"
                                                        placeholder="Select a Stop..."
                                                    />
                                                )}
                                            />
                                        )}
                                    />
                                </SoftBox>
                            </Grid>
                            {selected_device.stop?.stop_label && (
                                <Grid item xxs={12} lg={6}>
                                    <SoftBox pl={1} my={0.5}>
                                        <SoftTypography variant="body2" fontWeight='bold'>Stop Label</SoftTypography>
                                    </SoftBox>
                                    <SoftBox>
                                        <SoftInput placeholder="Stop Label" {...register('stop_label')} />
                                    </SoftBox>
                                </Grid>
                            )}
                        </Grid>
                    )}
                    {selected_device.tts.enabled == 1 && (
                        <Grid container spacing={2} px={2} py={1}>
                            <Grid item xxs={12}>
                                <SoftBox my={1.5}>
                                    <SoftBox pl={1}>
                                        <SoftTypography variant='body2' fontWeight='bold'>
                                            TTS Volume
                                        </SoftTypography>
                                    </SoftBox>
                                    <SoftBox>
                                        <Controller
                                            name="tts_volume"
                                            control={control}
                                            defaultValue={defaultValues.tts_volume}
                                            render={({ field }) => <Slider
                                                step={1} marks min={1} max={9}
                                                valueLabelDisplay="auto"
                                                valueLabelFormat={(value) => `${value * 10}%`}
                                                sx={{ py: 1.25, "& .MuiSlider-rail": { opacity: 1, }, '& .MuiSlider-valueLabel': { color: '#fff' } }}
                                                onChange={(e) => {
                                                    console.log(e.target.value);
                                                    field.onChange(e.target.value);
                                                    // var new_data = deviceData;
                                                    // new_data.tts_volume = (e.target.value);
                                                    // setDeviceData(new_data);
                                                }}
                                                value={field.value == null ? defaultValues.tts_volume : field.value}
                                            />
                                            }
                                        />
                                    </SoftBox>
                                </SoftBox>
                            </Grid>
                        </Grid>
                    )}
                    <Grid container spacing={2} px={2} py={1}>
                        <Grid item xxs={12} lg={6}>
                            <SoftBox pl={1} my={1.5}>
                                <SoftBox>
                                    <SoftTypography variant="body2" fontWeight='bold'>Active</SoftTypography>
                                </SoftBox>
                                <SoftBox>
                                    <Controller
                                        name="active"
                                        control={control}
                                        defaultValue={defaultValues.active}
                                        render={({ field }) => <Switch
                                            onChange={(e) => {
                                                console.log(e.target.checked);
                                                field.onChange(e.target.checked);
                                                // var new_data = deviceData;
                                                // new_data['active'] = !new_data['active'];
                                                // setDeviceData(new_data)
                                            }}
                                            checked={field.value == null ? defaultValues.active : field.value}
                                        />}
                                    />
                                </SoftBox>
                            </SoftBox>
                        </Grid>
                    </Grid>
                </SoftBox>
                <SoftBox>
                    <Divider />
                    <SoftBox sx={{ background: '#f9f9f9' }} display='flex' flexDirection='row' justifyContent='space-between' p={2}>
                        <SoftButton variant="gradient" color="dark" onClick={() => { triggerFormSubmit(); }}>Save</SoftButton>
                        <SoftButton variant="gradient" color="dark" onClick={() => { triggerDeviceReboot(); }}>Reboot Device</SoftButton>
                    </SoftBox>
                </SoftBox>
            </SoftBox>
        </form>
    );
}

DeviceSettingsForm.propTypes = {
    selected_device: PropTypes.object,
    deviceLayout: PropTypes.string,
    tags: PropTypes.object
};

export default DeviceSettingsForm;